import React, { useEffect, useRef } from 'react';

import { StudentGMetrixTableDialog, StudentGMetrixTableDialogHandle } from 'components/StudentGMetrixTableDialog/StudentGMetrixTableDialog';
import { AssignedStudent, TransactionApi, TransactionDetails } from 'apis/TransactionApi';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { useAccessCodes, useTransactionDetails } from 'pages/access_codes/context';
import { NewStudentForm } from 'components/NewStudentForm/NewStudentForm';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { useRegisterHeaderControls } from 'components/layout';
import { FormattedMessage, useIntl } from 'react-intl';
import { Country, CountryApi } from 'apis/CountryApi';
import { ToolTip } from 'components/ToolTip/ToolTip';
import { useNotification } from 'app/notifications';
import { filterDataBySearchTerm } from 'utils';
import { useNavigate } from 'react-router-dom';
import { GMetrixTable } from 'components';
import { useJoyride } from 'app/joyride';
import { Modal } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/access_codes/components/StudentsTab/styles/students-tab.scss';

interface StudentsTabProps {
    contentTiedToInactiveOrder: boolean;
    mode: 'Edit' | 'View';
    transactionWithDetails?: TransactionDetails;
}

export function StudentsTab(props: StudentsTabProps) {

    const { contentTiedToInactiveOrder } = props;

    const [state, setState] = useSuperState({
        countries: [] as Country[],
        searchTerm: '',
        showModal: false
    });

    const { countries, searchTerm, showModal } = state;

    const { userPartners } = useApp();

    const { pageContext, update } = useTransactionDetails();
    const { permissions, studentPermissions } = useAccessCodes();
    const { formatMessage } = useIntl();
    const { catchWithToast } = useNotification();
    const { loadCurrentTour } = useJoyride();

    const navigate = useNavigate();

    const gmetrixConfirmRef = useRef(null);
    const addStudentsDialogRef = useRef<StudentGMetrixTableDialogHandle>(null);

    useEffect(() => {

        getCountries();
        loadCurrentTour('StudentTab');

    }, []);

    const getCountries = async () => {

        const countries = await CountryApi.GetAll();
        setState({ countries });

    };

    const refreshStudentList = async () => {

        if (pageContext) {

            const partnerId = pageContext.Data.transactionDetails?.PartnerId ?? null;
            const students = await TransactionApi.GetStudentsByTransactionId(pageContext.Id, 1, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_student_list.bb95052d', defaultMessage: 'Failed to load student list.' })));
            const transactionDetails = await TransactionApi.Get(pageContext.Id, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_access_code_details.ed1f3281', defaultMessage: 'Failed to load access code details.' })));
            if (students && transactionDetails) {

                update({ students: students, transactionDetails: transactionDetails });

            }

        }

    };

    const removeStudentOnClick = async (studentContentAccessId: number) => {

        if (pageContext && pageContext.Data.students && studentContentAccessId !== 0) {

            await TransactionApi.RemoveStudentFromTransaction([studentContentAccessId]).catch(catchWithToast(formatMessage({ id: 'failed_to_remove_student_from_access_cod.76de4c3b', defaultMessage: 'Failed to remove student from access code.' })));

            update({
                students: [...pageContext.Data.students].filter(
                    student => student.StudentContentAccessId !== studentContentAccessId
                )
            });
            refreshStudentList();

        }

    };

    const onDetailsClick = (dataItem: AssignedStudent) => {

        navigate(`/students/${dataItem.Id}`);

    };

    const getShowToggle = (dataItem: AssignedStudent) => {

        // toggle is disabled (show reactivation button disabled)
        if (!dataItem.Active && dataItem.TotalUsesRemaining === 0) {

            return (
                <div className="py-3 me-1">
                    <ToolTip content={formatMessage({ id: 'no_remaining_uses_to_reactivate.d928c590', defaultMessage: 'No remaining uses to reactivate' })} placement="left"></ToolTip>
                </div>
            );

        } else if (!dataItem.Active && dataItem.TotalUsesRemaining > 0) {

            // toggle is Active (show reactivation button)
            return (
                <GMetrixButton className="m-1" onClick={() => activateStudent(dataItem)} variant="outline-primary">
                    <FormattedMessage defaultMessage="Reactivate" id="reactivate.1be3d76f" />
                </GMetrixButton>
            );

        } else if (dataItem.Active) {

            // toggle is Active (show remove button)
            return (
                <td>
                    <div className="d-flex justify-content-end mt-1 mb-1">
                        <GMetrixButton
                            className="me-2"
                            iconClass="fa-regular fa-arrow-up-right-from-square"
                            onClick={() => onDetailsClick(dataItem)}
                            variant="outline-primary"
                        >
                            <FormattedMessage defaultMessage="Details" id="details.7799b923" />
                        </GMetrixButton>
                        <GMetrixButton
                            onClick={async () => {

                                const result = await (gmetrixConfirmRef.current as any).confirm(formatMessage({ id: 'remove_student_from_access_code.61e0c47b', defaultMessage: 'Remove Student from Access Code' }), formatMessage({ id: 'are_you_sure_you_want_to_remove_this_stu.cd95e6fc', defaultMessage: 'Are you sure you want to remove this Student from this Access Code?' }));
                                if (result) {

                                    removeStudentOnClick(dataItem.StudentContentAccessId);

                                }

                            }}
                            iconClass="fa-regular fa-trash"
                            variant="outline-danger"
                        >

                        </GMetrixButton>
                    </div>
                </td>
            );

        }

    };

    const activateStudent = async (dataItem: any) => {

        if (pageContext?.Data.transactionDetails?.Id) {

            try {

                await TransactionApi.AddStudentToTransaction([pageContext?.Data.transactionDetails?.Id], [dataItem.Id], pageContext.Data.transactionDetails?.PartnerId);
                refreshStudentList();

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_activate_student.a3ce1dd9', defaultMessage: 'Failed to activate student.' }))(e);

            }

        }

    };

    const getAddStudentsCta = () => {

        if (permissions.Edit === false || contentTiedToInactiveOrder) {

            return null;

        }

        const transactionDetails = pageContext?.Data.transactionDetails;
        const currentStudentCount = pageContext?.Data.students?.filter(s => s.Active).length || 0;
        const totalUsesRemaining = transactionDetails?.UsesRemaining || 0;
        const maxStudents = transactionDetails?.IndividualMaxStudents;

        let isDisabled = false;
        let disabledMessage = '';

        // Check if max students limit is hit
        if (maxStudents !== null && maxStudents !== undefined && currentStudentCount >= maxStudents) {

            isDisabled = true;
            disabledMessage = `Maximum student limit reached (${maxStudents} students). No additional students can be added to this access code.`;

        }

        // Check if there are no uses remaining
        if (totalUsesRemaining <= 0) {

            isDisabled = true;
            disabledMessage = 'No remaining uses left on this access code. Please add more uses to add more students.';

        }

        return {
            actions: [
                {
                    label: formatMessage({ id: 'add_existing_students.c9b8b5e7', defaultMessage: 'Add Existing Student(s)' }),
                    onClick: async () => {

                        await addStudentsDialogRef.current?.show({
                            removeTheseStudents: pageContext?.Data.students?.map(s => s.Id) ?? [],
                            studentAssociation: { associationIds: [pageContext!.Id], associationName: 'Transaction' }
                        });
                        refreshStudentList();

                    }
                },
                {
                    label: formatMessage({ id: 'import_students.f041ccf5', defaultMessage: 'Import Students' }),
                    onClick: () => navigate(`/students/import?context=accessCodes&transactionId=${pageContext?.Id}&code=${encodeURIComponent(pageContext?.Data.transactionDetails?.Codes || '')}`)
                },
                {
                    label: formatMessage({ id: 'new_student.411d2e15', defaultMessage: 'New Student' }),
                    onClick: () => setState({ showModal: true })
                }
            ],
            disabled: isDisabled,
            label: (
                <div className="d-inline-flex align-items-center justify-content-center flex-fill gap-1">
                    <i className="fa-solid fa-plus" />
                    <span>

                        <FormattedMessage defaultMessage="Add Students" id="add_students.00f523a7" />
                    </span>
                </div>
            ),
            popoverContent: disabledMessage,
            renderPopover: isDisabled
        };

    };

    const addStudentsCta = getAddStudentsCta();

    // Register the "Add Students" CTA to the access code detail page header.
    useRegisterHeaderControls(addStudentsCta ? { cta: addStudentsCta } : null);

    // The Access Codes list's "Number of Students" column counts active students only,
    // while this tab also lists removed/used-up students; show both so the counts reconcile.
    const activeStudentCount = pageContext?.Data.students?.filter(s => s.Active).length ?? 0;
    const totalStudentCount = pageContext?.Data.students?.length ?? 0;

    return (
        <>
            {pageContext && pageContext.Data.students && (
                <>
                    <div className="text-muted mb-2">
                        {totalStudentCount !== activeStudentCount
                            ? (
                                <FormattedMessage
                                    defaultMessage="{activeCount, plural, one {# active student} other {# active students}} ({totalCount} total, including removed or used-up students)" id="activecount_plural_one_active_student_ot.62e94635"
                                    values={{ activeCount: activeStudentCount, totalCount: totalStudentCount }}
                                />
                            )
                            : (
                                <FormattedMessage
                                    defaultMessage="{activeCount, plural, one {# active student} other {# active students}}" id="activecount_plural_one_active_student_ot.59c38f3c"
                                    values={{ activeCount: activeStudentCount }}
                                />
                            )}
                    </div>
                    <GMetrixTable
                        allowExport
                        columnData={[
                            { field: 'Fullname', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                            { field: 'Username' },
                            {
                                cell: ({ dataItem }) => (
                                    <td>
                                        <div className="d-flex justify-content-start">
                                            {(dataItem as any).EmailAddress}
                                        </div>
                                    </td>
                                ), field: 'ContentAccessTypeName', title: formatMessage({ id: 'email.5cbb5845', defaultMessage: 'Email', description: 'Formulario' })
                            },
                            { field: 'Active', title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }) },
                            {
                                cell: ({ dataItem }) => (
                                    <td>
                                        <div className="d-flex justify-content-end">
                                            {/* returns the toggle boolean */}
                                            {getShowToggle(dataItem)}
                                        </div>
                                    </td>
                                ),
                                hidden: permissions.Edit === false || contentTiedToInactiveOrder,
                                width: '200px'
                            }
                        ]}
                        bottomBarTableName={formatMessage({ id: 'students.0933e5a0', defaultMessage: 'Students' })}
                        loading={false}
                        rowData={filterDataBySearchTerm(searchTerm, pageContext.Data.students)}
                        searchTerm={searchTerm}
                        setSearchTerm={val => setState({ searchTerm: val })}

                    />

                    <StudentGMetrixTableDialog
                        multiSelect
                        actionButtonText={formatMessage({ id: 'add_students.00f523a7', defaultMessage: 'Add Students' })}
                        option="Add"
                        partnerId={pageContext?.Data.transactionDetails?.PartnerId}
                        permissions={permissions}
                        ref={addStudentsDialogRef}
                    />
                    <Modal onHide={() => setState({ showModal: false })} show={showModal} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FormattedMessage defaultMessage="Create Student" id="create_student.b6877d5c" />
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <NewStudentForm
                                onSuccess={async student => {

                                    await TransactionApi.AddStudentToTransaction([pageContext.Id], [student?.Id], pageContext.Data.transactionDetails?.PartnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_add_student_to_access_code.d2f08719', defaultMessage: 'Failed to add student to access code.' })));
                                    refreshStudentList();
                                    setState({ showModal: false });

                                }}
                                ActionsWrapper={Modal.Footer}
                                ContentWrapper={Modal.Body}
                                countries={countries}
                                onClose={() => setState({ showModal: false })}
                                partnerId={pageContext?.Data.transactionDetails?.PartnerId}
                                permissions={studentPermissions}
                                userPartners={userPartners}
                            />
                        </Modal.Body>

                    </Modal>

                    <GMetrixDialogConfirm ref={gmetrixConfirmRef} />
                </>
            )}
        </>
    );

}
